/**
 * Inline Auth Handler
 * Handles login/signup forms that appear inline within pages (generator, dashboard)
 * instead of redirecting to separate auth pages.
 */

import { supabase } from './client.js';
import { authState } from './state.js';

/**
 * Initialize inline auth for a page
 * @param {Object} options Configuration options
 * @param {string} options.formId - ID of the login form
 * @param {string} options.emailId - ID of email input
 * @param {string} options.passwordId - ID of password input
 * @param {string} options.errorId - ID of error message element
 * @param {string} options.submitId - ID of submit button
 * @param {string} options.toggleId - ID of password toggle button
 * @param {string} options.googleId - ID of Google OAuth button
 * @param {Function} options.onSuccess - Callback when auth succeeds
 */
export function initInlineAuth(options) {
  const {
    formId = 'inline-login-form',
    emailId = 'inline-email',
    passwordId = 'inline-password',
    errorId = 'inline-error-message',
    submitId = 'inline-submit-btn',
    toggleId = 'inline-toggle-password',
    googleId = 'inline-google-login',
    onSuccess
  } = options;

  const form = document.getElementById(formId);
  const emailInput = document.getElementById(emailId);
  const passwordInput = document.getElementById(passwordId);
  const errorEl = document.getElementById(errorId);
  const submitBtn = document.getElementById(submitId);
  const toggleBtn = document.getElementById(toggleId);
  const googleBtn = document.getElementById(googleId);

  if (!form) {
    console.warn('[InlineAuth] Form not found:', formId);
    return;
  }

  // Password visibility toggle
  if (toggleBtn && passwordInput) {
    toggleBtn.addEventListener('click', () => {
      const isPassword = passwordInput.type === 'password';
      passwordInput.type = isPassword ? 'text' : 'password';
      toggleBtn.textContent = isPassword ? 'HIDE' : 'SHOW';
    });
  }

  // Form submit handler
  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const email = emailInput?.value?.trim();
    const password = passwordInput?.value;

    if (!email || !password) {
      showError(errorEl, 'Please enter email and password');
      return;
    }

    // Show loading state
    setLoading(submitBtn, true);
    hideError(errorEl);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw error;
      }

      // Success - reinitialize auth state
      await authState.init();

      // Call success callback
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(data.user);
      }

    } catch (error) {
      console.error('[InlineAuth] Login error:', error);
      showError(errorEl, getErrorMessage(error));
    } finally {
      setLoading(submitBtn, false);
    }
  });

  // Google OAuth handler
  if (googleBtn) {
    googleBtn.addEventListener('click', async () => {
      try {
        const { error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: window.location.href // Return to current page
          }
        });

        if (error) {
          throw error;
        }
      } catch (error) {
        console.error('[InlineAuth] Google login error:', error);
        showError(errorEl, getErrorMessage(error));
      }
    });
  }
}

/**
 * Show error message
 */
function showError(el, message) {
  if (el) {
    el.textContent = message;
    el.classList.remove('hidden');
  }
}

/**
 * Hide error message
 */
function hideError(el) {
  if (el) {
    el.classList.add('hidden');
    el.textContent = '';
  }
}

/**
 * Set loading state on button
 */
function setLoading(btn, loading) {
  if (btn) {
    btn.disabled = loading;
    btn.classList.toggle('loading', loading);
  }
}

/**
 * Get user-friendly error message
 */
function getErrorMessage(error) {
  const message = error?.message || 'An error occurred';

  // Map common Supabase errors to friendly messages
  if (message.includes('Invalid login credentials')) {
    return 'Invalid email or password';
  }
  if (message.includes('Email not confirmed')) {
    return 'Please check your email to confirm your account';
  }
  if (message.includes('Too many requests')) {
    return 'Too many attempts. Please wait a moment.';
  }

  return message;
}

/**
 * Check if user is authenticated
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated() {
  await authState.init();
  return authState.isLoggedIn;
}

/**
 * Show auth screen, hide content
 * @param {string} authScreenId - ID of auth screen element
 * @param {string} contentId - ID of content element to hide
 */
export function showAuthScreen(authScreenId = 'auth-required-screen', contentId = 'generator-content') {
  const authScreen = document.getElementById(authScreenId);
  const content = document.getElementById(contentId);

  if (authScreen) authScreen.classList.remove('hidden');
  if (content) content.classList.add('hidden');
}

/**
 * Hide auth screen, show content
 * @param {string} authScreenId - ID of auth screen element
 * @param {string} contentId - ID of content element to show
 */
export function hideAuthScreen(authScreenId = 'auth-required-screen', contentId = 'generator-content') {
  const authScreen = document.getElementById(authScreenId);
  const content = document.getElementById(contentId);

  if (authScreen) authScreen.classList.add('hidden');
  if (content) content.classList.remove('hidden');
}

export default {
  initInlineAuth,
  isAuthenticated,
  showAuthScreen,
  hideAuthScreen
};
